/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router";
import { Link } from "../components/ui/Link";
import { CornerDownLeft, ListOrdered, MessageSquareText } from "lucide-react";
import {
  claimHandoff,
  getStream,
  resolveHandoff,
  type Stream,
} from "../api/streams";
import type { RunProvider } from "../api/runs";
import type {
  Handoff,
  SkippedCandidate,
  StreamMessage,
  StreamState,
} from "../lib/stream";
import { stateLabel } from "../lib/stream";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { fromOf } from "../lib/navigation";
import { useToast } from "../contexts/ToastContext";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { ConfidenceRing } from "../components/ui/ConfidenceRing";
import { ConfidenceBreakdown } from "../components/ui/ConfidenceBreakdown";
import { CopyableId } from "../components/ui/CopyableId";
import { HealthieLink } from "../components/HealthieLink";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Spinner } from "../components/ui/Spinner";
import { Tooltip } from "../components/ui/Tooltip";
import { formatSlot, runTime, triggerLabel } from "../lib/runs";
import { cn } from "../lib/utils";

const POLL_MILLIS = 30_000;

/**
 * One stream, told as the conversation it is: the agent reaches out, families reply by text, and
 * when it isn't sure it turns to you. Resolving is simply the next reply in the thread.
 */
const BACK_LABELS: Record<string, string> = { "/inbox": "Inbox", "/runs": "Runs" };

function backOf(state: unknown): { to: string; label: string } {
  const from = fromOf(state);
  const label = from === null ? undefined : BACK_LABELS[from.pathname];
  return from !== null && label !== undefined
    ? { to: from.pathname, label }
    : { to: "/inbox", label: "Inbox" };
}

export function StreamDetail() {
  const { id } = useParams();
  const location = useLocation();
  const back = backOf(location.state);
  const [stream, setStream] = useState<Stream | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (id === undefined) {
      return;
    }
    const result = await getStream(id);
    if (result.ok) {
      setStream(result.value);
      setError(null);
    } else {
      setError(result.error.message);
    }
  }, [id]);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_MILLIS);
    return () => clearInterval(timer);
  }, [refresh]);

  useChrome(
    {
      back,
      title: stream?.subjectName,
      sidePanel: stream ? <StreamAside stream={stream} /> : undefined,
      hideBottomNav: true,
    },
    [
      stream?.id,
      stream?.subjectName,
      stream?.state,
      stream?.handoffs.length,
      back.to,
    ],
  );

  if (stream === null && error === null) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (stream === null) {
    return (
      <div className="max-w-2xl">
        <ErrorMessage message={error} className="mb-0" />
      </div>
    );
  }

  const breakdownHandoff = stream.handoffs.find(
    (handoff) => handoff.confidenceBreakdown !== null,
  );

  return (
    <div className="space-y-8">
      <Header stream={stream} />

      <CompactFacts stream={stream} />

      {stream.messages.length > 0 && (
        <div className="space-y-6">
          {stream.messages.map((message) => (
            <FlowStep key={message.id} message={message} />
          ))}
        </div>
      )}

      {breakdownHandoff?.confidenceBreakdown && (
        <div className="enter rounded-2xl border border-border bg-bg-elevated p-5 xl:hidden">
          <ConfidenceBreakdown
            breakdown={breakdownHandoff.confidenceBreakdown}
            confidence={breakdownHandoff.confidence}
          />
        </div>
      )}

      {stream.handoffs.length > 0 ? (
        stream.handoffs.map((handoff) => (
          <ActionPanel
            key={handoff.entryId}
            handoff={handoff}
            onChanged={refresh}
          />
        ))
      ) : (
        <p className="text-sm text-text-tertiary">
          {stream.state === "DONE"
            ? "This case is closed."
            : "The agent has it from here."}
        </p>
      )}
    </div>
  );
}

/**
 * Below xl there is no side panel, so the essentials ride in one quiet line under the header:
 * when the run opened, how many families were texted, and the provider with their Healthie link.
 * The run id stays xl-only; nobody needs it on a small screen.
 */
function CompactFacts({ stream }: { stream: Stream }) {
  const contacted = stream.messages.filter((m) =>
    m.id.startsWith("offer:"),
  ).length;
  return (
    <div className="enter flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-secondary xl:hidden">
      <span>Opened {runTime(stream.startedAt)}</span>
      {contacted > 0 && (
        <>
          <span className="text-text-tertiary" aria-hidden>
            ·
          </span>
          <span>
            {contacted} {contacted === 1 ? "family" : "families"} contacted
          </span>
        </>
      )}
      {stream.provider !== null && (
        <>
          <span className="text-text-tertiary" aria-hidden>
            ·
          </span>
          <ProviderLine provider={stream.provider} />
        </>
      )}
    </div>
  );
}

function StreamAside({ stream }: { stream: Stream }) {
  const contacted = stream.messages.filter((m) =>
    m.id.startsWith("offer:"),
  ).length;
  const breakdownHandoff = stream.handoffs.find(
    (handoff) => handoff.confidenceBreakdown !== null,
  );
  return (
    <div className="space-y-6">
      <dl className="divide-y divide-border/50">
        <Field label="Freed slot">
          {stream.slotStartsAt !== null
            ? formatSlot(stream.slotStartsAt, stream.slotEndsAt)
            : "No slot attached"}
        </Field>
        <Field label="Opened">{runTime(stream.startedAt)}</Field>
        {contacted > 0 && (
          <Field label="Outreach">
            {contacted} {contacted === 1 ? "family" : "families"} contacted
          </Field>
        )}
        {stream.provider !== null && (
          <Field label="Provider">
            <ProviderLine provider={stream.provider} />
          </Field>
        )}
        <Field label="Run">
          <span className="break-all font-mono text-xs text-text-tertiary">
            {stream.id}
          </span>
        </Field>
      </dl>
      {breakdownHandoff?.confidenceBreakdown && (
        <ConfidenceBreakdown
          breakdown={breakdownHandoff.confidenceBreakdown}
          confidence={breakdownHandoff.confidence}
          className="border-t border-border/50 pt-5"
        />
      )}
    </div>
  );
}

function ProviderLine({ provider }: { provider: RunProvider }) {
  const name = [provider.firstName, provider.lastName]
    .filter((part) => part !== null)
    .join(" ");
  return (
    <span className="flex flex-wrap items-center gap-2">
      {provider.healthieId !== null ? (
        <HealthieLink
          healthieId={provider.healthieId}
          className="text-accent underline-offset-2 hover:underline"
        >
          {name === "" ? `Provider ${provider.healthieId}` : name}
        </HealthieLink>
      ) : (
        <span>{name === "" ? "Unknown provider" : name}</span>
      )}
      {provider.licensedStates.length > 0 && (
        <span className="text-[11px] text-text-tertiary">
          {provider.licensedStates.join(" · ")}
        </span>
      )}
      {!provider.perStatePayerTags && (
        <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-warning">
          no per-state payer tags
        </span>
      )}
    </span>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <dt className="text-[11px] font-medium uppercase tracking-[0.13em] text-text-tertiary">
        {label}
      </dt>
      <dd className="mt-1.5 text-sm leading-relaxed text-text-primary">
        {children}
      </dd>
    </div>
  );
}

function Header({ stream }: { stream: Stream }) {
  return (
    <header className="enter space-y-2.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="font-display text-4xl leading-tight text-text-primary">
          {stream.subjectName}
        </h1>
        <StatePill state={stream.state} />
      </div>
      <p className="text-sm text-text-secondary">
        {triggerLabel(stream.trigger)}
        {stream.slotStartsAt !== null && (
          <>
            {" "}
            · freed slot {formatSlot(stream.slotStartsAt, stream.slotEndsAt)}
          </>
        )}
      </p>
      {(stream.patientExternalId !== null || stream.patientId !== null) && (
        <div className="flex flex-wrap gap-2 pt-1">
          {stream.patientExternalId !== null && (
            <CopyableId label="Healthie" value={stream.patientExternalId} />
          )}
          {stream.patientId !== null && (
            <CopyableId label="Patient" value={stream.patientId} />
          )}
        </div>
      )}
    </header>
  );
}

function StatePill({ state }: { state: StreamState }) {
  const attention = state === "AWAITING_HUMAN";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
        attention
          ? "bg-bg-elevated text-text-primary ring-1 ring-border"
          : "text-text-secondary",
      )}
    >
      <span
        className={cn(
          "size-1.5 rounded-full",
          attention ? "bg-text-primary" : "bg-text-tertiary",
        )}
      />
      {stateLabel(state)}
    </span>
  );
}

/**
 * One step in the agent's flow: an action it took, or a reply it received. A log, not a chat —
 * the agent's steps carry a thin accent rule, the family's a neutral one.
 */
function FlowStep({ message }: { message: StreamMessage }) {
  const [rankingOpen, setRankingOpen] = useState(false);

  if (message.author === "SYSTEM") {
    return (
      <p className="enter text-sm text-text-tertiary">
        {message.text}
        {message.at !== null && <span> · {runTime(message.at)}</span>}
      </p>
    );
  }

  const agent = message.author === "AGENT";
  const ranking = message.ranking;
  const hasShortlist = ranking !== undefined && ranking.shortlist.length > 0;
  const isRanking =
    ranking !== undefined && (hasShortlist || ranking.skipped.length > 0);

  return (
    <div
      className={cn(
        "enter border-l-2 pl-4",
        agent ? "border-accent/45" : "border-border",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 text-xs text-text-tertiary">
        <span className="font-medium text-text-secondary">
          {agent ? "Scheduling agent" : message.name}
        </span>
        {message.viaText && (
          <span className="inline-flex items-center gap-1">
            <MessageSquareText size={11} />
            by text
          </span>
        )}
        {message.at !== null && <span>· {runTime(message.at)}</span>}
      </div>
      <p className="mt-1.5 text-[15px] leading-relaxed text-text-primary">
        {message.text}
      </p>
      {isRanking ? (
        <button
          type="button"
          onClick={() => setRankingOpen(true)}
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
        >
          <ListOrdered size={13} />
          {hasShortlist
            ? "View ranking"
            : `Considered but excluded (${ranking.skipped.length})`}
        </button>
      ) : (
        message.detail.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-xs text-text-tertiary">
            {message.detail.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )
      )}
      {message.poolStates !== undefined && message.poolStates.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {message.poolStates.map((state) => (
            <Link
              key={state}
              to={`/agents/scheduling?state=${state}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
            >
              View the current {state} waitlist
            </Link>
          ))}
        </div>
      )}
      {isRanking && (
        <Modal
          isOpen={rankingOpen}
          onClose={() => setRankingOpen(false)}
          title={hasShortlist ? "Waitlist ranking" : "Considered but excluded"}
          size="sm"
          asSheet={false}
        >
          <div className="space-y-6 px-6 py-5">
            {hasShortlist && (
              <div>
                <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-tertiary">
                  Contacted, best match first
                </h3>
                <ol className="space-y-2">
                  {ranking.shortlist.map((entry) => (
                    <ShortlistRow key={entry.rank} familyId={entry.familyId}>
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/12 text-xs font-semibold text-accent">
                        {entry.rank}
                      </span>
                      <span className="flex-1 truncate text-sm font-medium text-text-primary">
                        {entry.name}
                        {entry.acuity !== null && (
                          <span className="ml-2 font-normal text-text-tertiary">
                            {entry.acuity} acuity
                          </span>
                        )}
                      </span>
                      {entry.fromTransition && (
                        <span className="shrink-0 rounded-full bg-accent/12 px-2 py-0.5 text-[11px] font-medium text-accent">
                          provider transition
                        </span>
                      )}
                      <span className="shrink-0 text-xs tabular-nums text-text-secondary">
                        match {entry.score.toFixed(2)}
                      </span>
                    </ShortlistRow>
                  ))}
                </ol>
              </div>
            )}

            {ranking.skipped.length > 0 && (
              <div>
                <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-tertiary">
                  Skipped
                </h3>
                <ul className="space-y-1.5">
                  {ranking.skipped.map((entry, index) => (
                    <SkippedRow key={`${entry.name}:${index}`} entry={entry} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function ShortlistRow({
  familyId,
  children,
}: {
  familyId: number | null;
  children: React.ReactNode;
}) {
  const rowClass =
    "flex items-center gap-3 rounded-xl border border-border bg-bg-base px-3 py-2.5";
  if (familyId === null) {
    return <li className={rowClass}>{children}</li>;
  }
  return (
    <li>
      <Link
        to={`/care/families/${familyId}`}
        className={`${rowClass} transition-colors hover:border-accent/40 hover:bg-accent/5`}
      >
        {children}
      </Link>
    </li>
  );
}

function SkippedRow({ entry }: { entry: SkippedCandidate }) {
  const body = (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-text-primary">
          {entry.name}
        </span>
        {entry.payer !== null && (
          <span className="rounded-full bg-bg-muted px-2 py-0.5 text-[11px] text-text-secondary">
            {entry.payer.displayName}
          </span>
        )}
      </div>
      <p className="mt-0.5 text-sm leading-relaxed text-text-secondary">
        {entry.detail}
      </p>
      {entry.providerAccepts !== null && (
        <p className="mt-0.5 text-xs text-text-tertiary">
          {entry.providerAccepts.length === 0
            ? "This provider has no accepted payers on file for that state."
            : `Provider accepts: ${entry.providerAccepts.map((p) => p.displayName).join(", ")}`}
        </p>
      )}
    </>
  );
  if (entry.familyId === null) {
    return (
      <li className="rounded-xl border border-border/60 px-3.5 py-2.5">
        {body}
      </li>
    );
  }
  return (
    <li>
      <Link
        to={`/care/families/${entry.familyId}`}
        className="block rounded-xl border border-border/60 px-3.5 py-2.5 transition-colors hover:border-accent/40 hover:bg-accent/5"
      >
        {body}
      </Link>
    </li>
  );
}

function ActionPanel({
  handoff,
  onChanged,
}: {
  handoff: Handoff;
  onChanged: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const { session } = useAuth();
  const claimedByMe =
    handoff.claimed &&
    session.state === "authenticated" &&
    handoff.claimedByUserId === session.user.id;
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<"claim" | "resolve" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    noteRef.current?.focus({ preventScroll: true });
  }, []);

  async function run(
    kind: "claim" | "resolve",
    action: () => Promise<{ ok: boolean; error?: { message: string } }>,
  ) {
    setBusy(kind);
    setActionError(null);
    const result = await action();
    if (!result.ok && result.error) {
      setActionError(result.error.message);
      showToast("error", result.error.message);
      setBusy(null);
      return;
    }
    showToast(
      "success",
      kind === "claim" ? "You claimed this case" : "Case resolved",
    );
    await onChanged();
    setBusy(null);
  }

  const canResolve = busy === null && note.trim() !== "";

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-bg-elevated p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-tertiary">
          Your decision
        </h2>
        {handoff.claimed && (
          <span className="text-[11px] font-medium text-text-secondary">
            {claimedByMe ? "You claimed this" : "A coordinator has this"}
          </span>
        )}
      </div>
      {actionError !== null && (
        <p role="alert" className="text-xs text-error">
          {actionError}
        </p>
      )}
      <textarea
        ref={noteRef}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        autoComplete="off"
        rows={3}
        placeholder="What did you decide, and why?"
        className="w-full resize-none rounded-xl border border-border bg-bg-elevated px-3.5 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-hover focus:outline-none focus:ring-2 focus:ring-text-tertiary/20"
      />
      <div className="flex items-center justify-between gap-3">
        {handoff.confidence !== null ? (
          <ConfidenceRing value={handoff.confidence} />
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          {!handoff.claimed && (
            <Tooltip
              className="max-w-[280px] whitespace-normal"
              content="Take this case so other coordinators know you have it."
            >
              <Button
                variant="ghost"
                size="sm"
                disabled={busy !== null}
                onClick={() =>
                  void run("claim", () => claimHandoff(handoff.entryId))
                }
              >
                {busy === "claim" ? (
                  <>
                    <Spinner size="xs" />
                    Claiming…
                  </>
                ) : (
                  "Claim"
                )}
              </Button>
            </Tooltip>
          )}
          <Tooltip
            content={
              canResolve || busy === "resolve"
                ? "Close this case. Your note becomes the record of what was decided."
                : "Write a note first. It becomes the record of what was decided."
            }
          >
            <Button
              variant="primary"
              size="sm"
              disabled={!canResolve}
              onClick={() =>
                void run("resolve", () =>
                  resolveHandoff(handoff.entryId, note.trim()),
                )
              }
            >
              {busy === "resolve" ? (
                <>
                  <Spinner size="xs" className="text-bg-base" />
                  Resolving…
                </>
              ) : (
                <>
                  Resolve
                  <CornerDownLeft size={14} />
                </>
              )}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
